/**
 * Story 7.19 (AC3, AC5, Task 10/11) — the retro-link + GA4 User-ID hook.
 *
 * Fired from ONE place (`fetchCurrentUser` success, `src/ducks/user.duck.js`)
 * rather than from each of the four auth entry points directly — password
 * login/signup reach `fetchCurrentUser({ afterLogin: true })` through
 * `loginThunk`/`signupThunk`; IdP login/signup (`loginWithIdp.js`,
 * `createUserWithIdp.js`) reload the app, whose boot `fetchCurrentUser` fires
 * it; a long-lived session whose cookie gathered anonymous searches while
 * signed out is covered too. This file holds the shared logic so
 * `auth.duck.js`'s `logoutThunk` can clear the flag without importing
 * `user.duck.js` (which already imports FROM `auth.duck.js` — a second edge
 * the other way would be circular).
 */

import { getVisitorSessionId, resetVisitorSessionId } from './searchTelemetry';
import { linkVisitor } from './api';

const FLAG_PREFIX = 'tph_visitor_linked:';
const flagKey = userUuid => `${FLAG_PREFIX}${userUuid}`;

// Ticket 88 Part 1 (ledger #1376), Ryan's ruling: ONE VISITOR ID NEVER LINKS TO
// MORE THAN ONE ACCOUNT. This localStorage key records which account the
// CURRENT visitor id was linked for (a Sharetribe user uuid — the same
// pseudonymous identifier `log.setUserId` already carries). When a different
// account signs in on this browser — a session that lapsed without a logout —
// the id is rotated BEFORE the link call, so the second person never browses
// under the first person's id. tph-api refuses a cross-account link as well
// (`POST /api/v1/visitors/link`, reason `visitor_already_linked`), and that
// refusal is relayed by the proxy and acted on here (D8): a browser linked
// BEFORE this shipped carries no marker, so the marker alone could not rotate
// it; the refusal can. Both halves together make the ruling hold for every
// browser, old and new.
const OWNER_KEY = 'tph_visitor_owner';
const REFUSED_OTHER_ACCOUNT = 'visitor_already_linked';

const readOwner = () => {
  try {
    return window.localStorage.getItem(OWNER_KEY);
  } catch (e) {
    return null;
  }
};

const writeOwner = userUuid => {
  try {
    window.localStorage.setItem(OWNER_KEY, userUuid);
  } catch (e) {
    // localStorage blocked — the server-side refusal is the backstop.
  }
};

/**
 * GA4's native User-ID is set on every call (idempotent — just configures
 * subsequent events). The retro-link call is gated to AT MOST ONCE ANSWERED
 * per user per tab via a sessionStorage flag, wrapped in try/catch (storage
 * blocked, e.g. private browsing → call anyway; `linkVisitor`'s endpoint is
 * idempotent via tph-api's `ON CONFLICT DO NOTHING`, so a repeat call is
 * harmless, just wasteful).
 *
 * @param {string} [userUuid]
 * @param {Object} [options]
 * @param {boolean} [options.isLoggedInAs] true when this session is an
 *   operator's Console "login as" (`state.auth.isLoggedInAs`): nothing is set,
 *   written or sent (ticket 88 Part 2, ledger #1378).
 * @returns {Promise<void>|undefined} resolves once the link (and a D8 rotation
 *   retry, if any) has settled; never rejects. Callers ignore it; tests await it.
 */
export const setAnalyticsUserAndLinkVisitor = (userUuid, { isLoggedInAs = false } = {}) => {
  if (typeof window === 'undefined' || !userUuid) {
    return undefined;
  }
  // Ticket 88 Part 2 (ledger #1378, D-C1): an operator browsing AS a customer
  // (Console "login as") is not that customer. No GA4 User-ID, no owner
  // marker, no link — the operator's browsing is never credited to the account.
  // The caller reads the flag from `state.auth.isLoggedInAs`; the proxy refuses
  // such a session as well (server/api/visitor-link.js), so both halves hold.
  if (isLoggedInAs === true) {
    return undefined;
  }
  if (typeof window.gtag === 'function') {
    window.gtag('set', { user_id: userUuid });
  }
  // Ticket 88 (#1376): a visitor id already linked for ANOTHER account is
  // rotated before this account is linked — never the same id for two people.
  const owner = readOwner();
  if (owner && owner !== userUuid) {
    rotateVisitor();
  }
  if (owner !== userUuid) {
    writeOwner(userUuid);
    // A logout in ANOTHER tab cleared localStorage but not this tab's flag
    // (review F4): the marker is gone or different, so the flag is stale and
    // the freshly minted id must still be linked here.
    removeFlag(userUuid);
  }
  const key = flagKey(userUuid);
  if (hasFlag(key)) {
    return undefined;
  }
  // Two calls in this tab before the first answered (boot and a login in quick
  // succession) share the one in-flight link rather than posting twice.
  if (inFlight.has(key)) {
    return inFlight.get(key);
  }
  // Ticket 88 Part 2 (ledger #1379, D-C2): the per-tab flag is set only once
  // an ANSWER came back. A link that failed (the proxy answered a bodiless 204,
  // or the call never completed) leaves no flag, so the next fetchCurrentUser
  // in this tab tries again; the endpoint is idempotent, so a repeat is
  // harmless. Before this, the flag was set BEFORE the call and a failure was
  // never retried in the tab's lifetime.
  //
  // D8 — tph-api's answer. On `visitor_already_linked` this id belongs to
  // another account (linked before this browser carried a marker): rotate, own
  // the new id, and link it. Once; a refusal of a freshly minted id cannot
  // happen, so a second one is not retried.
  // The identity generation this call belongs to: a logout or rotation while
  // the call is in flight bumps it, and a late answer for the OLD identity is
  // then ignored — never rotating or re-owning the account that signed in
  // after it (cold review of PR #118, F2).
  const gen = generation;
  const pending = Promise.resolve(linkVisitor(getVisitorSessionId()))
    .then(answer => {
      if (generation !== gen) {
        return undefined;
      }
      if (answer && answer.reason === REFUSED_OTHER_ACCOUNT) {
        // This rotation is this call's own: it opens a new generation, which
        // the retry belongs to (an outside clear during the retry bumps it again).
        rotateVisitor();
        const retryGen = generation;
        inFlight.set(key, pending);
        writeOwner(userUuid);
        return Promise.resolve(linkVisitor(getVisitorSessionId())).then(second => {
          if (second && generation === retryGen) {
            setFlag(key);
          }
          return undefined;
        });
      }
      if (answer) {
        setFlag(key);
      }
      return undefined;
    })
    .catch(() => undefined)
    .finally(() => {
      if (inFlight.get(key) === pending) {
        inFlight.delete(key);
      }
    });
  inFlight.set(key, pending);
  return pending;
};

// One in-flight link per flag key, per tab (module state; a page load starts
// empty). Cleared when the call settles, whatever the answer, and forgotten
// by a logout or rotation, which also bumps the generation so that a late
// answer from before it is ignored.
const inFlight = new Map();
let generation = 0;

const hasFlag = key => {
  try {
    return Boolean(window.sessionStorage.getItem(key));
  } catch (e) {
    // sessionStorage blocked — never "already linked": call anyway (idempotent).
    return false;
  }
};

const setFlag = key => {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch (e) {
    // sessionStorage blocked — nothing to record.
  }
};

// The visitor id is removed (the next reader mints a fresh one) and every
// per-tab "already linked" flag goes with it, so the new id is linked.
const rotateVisitor = () => {
  resetVisitorSessionId();
  clearVisitorLinkedFlags();
};

const removeFlag = userUuid => {
  try {
    window.sessionStorage.removeItem(flagKey(userUuid));
  } catch (e) {
    // sessionStorage blocked — nothing stored, nothing to remove.
  }
};

/**
 * `logoutThunk` success clears EVERY `tph_visitor_linked:*` flag (8-17 AC-4
 * item 4 precedent) — not just the current user's, since this tab may carry a
 * stale flag from an earlier signed-in user.
 */
export const clearVisitorLinkedFlags = () => {
  if (typeof window === 'undefined') {
    return;
  }
  // A link still in flight belongs to the identity being cleared (a logout, a
  // rotation): forget it, so the next sign-in links the fresh id itself, and
  // bump the generation so its late answer changes nothing.
  inFlight.clear();
  generation += 1;
  try {
    const keys = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key && key.startsWith(FLAG_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach(key => window.sessionStorage.removeItem(key));
  } catch (e) {
    // sessionStorage blocked — nothing to clear.
  }
};

/**
 * Ticket 88 Part 1 (ledger #1376) — logout starts a fresh visitor. Called from
 * `logoutThunk` on success: the visitor id is removed (the next reader mints a
 * new one), the owner marker goes with it, and every `tph_visitor_linked:*`
 * flag is cleared. Never throws.
 */
export const resetVisitorIdentity = () => {
  if (typeof window === 'undefined') {
    return;
  }
  resetVisitorSessionId();
  try {
    window.localStorage.removeItem(OWNER_KEY);
  } catch (e) {
    // localStorage blocked — nothing stored, nothing to remove.
  }
  clearVisitorLinkedFlags();
};
